import { CurrencyRateDetails } from 'further-types/lp/transaction';

import { round } from 'lodash';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { ConsiderationTypeHoldingClassTypes } from 'further-types/lp/realisation';

export const calculateEquityAndFxGains = (
  investment: {
    holdingClassId: string;
    investmentId: string;
    receivedConsiderationTotal: number;
    escrowConsiderationTotal: number;
    earnoutConsiderationTotal: number;
    withholdingTaxPayable: number;
    investmentSaleUnwindTcy: number;
    investmentSaleUnwindLcy: number;
  },
  realisationCurrencyRate: CurrencyRateDetails | null,
  threeCyRealisationCurrencyRate: CurrencyRateDetails | null,
  considerationTypeHoldingClassType: ConsiderationTypeHoldingClassTypes,
) => {
  const totalConsiderationTcy =
    investment.receivedConsiderationTotal +
    investment.escrowConsiderationTotal +
    investment.withholdingTaxPayable;

  const realisationTransactionRate =
    realisationCurrencyRate?.overrideRate ??
    realisationCurrencyRate?.fetchedRate ??
    1;

  const threeCyRealisationTransactionRate =
    threeCyRealisationCurrencyRate?.overrideRate ??
    threeCyRealisationCurrencyRate?.fetchedRate ??
    1;

  // If we have a 3CY realisation we need to convert 3CY to TCY using the 3CY FX rate
  const fxRateAtTimeOfRealisation =
    considerationTypeHoldingClassType ===
    ConsiderationTypeHoldingClassTypes.ThreeCYNonLcyConsiderationNonLcyHoldingClass
      ? threeCyRealisationTransactionRate
      : realisationTransactionRate;

  // Calculate FX gain on the initial investment amount
  // FX gain = (initial investment at sale rate) - (initial investment at purchase rate)
  const fxPnlLcy = round(
    (fxRateAtTimeOfRealisation
      ? investment.investmentSaleUnwindTcy / fxRateAtTimeOfRealisation
      : 0) - investment.investmentSaleUnwindLcy,
    2,
  );

  // Total TCY consideration received from sale
  if (
    considerationTypeHoldingClassType ===
    ConsiderationTypeHoldingClassTypes.LcyHoldingClass
  ) {
    const totalConsiderationLcy = realisationTransactionRate
      ? totalConsiderationTcy / realisationTransactionRate
      : 0;

    const fxPnlLcy = 0;
    const overallPnlLcy =
      totalConsiderationLcy - investment.investmentSaleUnwindLcy;

    const equityPnlLcy = overallPnlLcy - fxPnlLcy;

    return {
      totalConsiderationTcy,
      totalConsiderationLcy,
      investmentSaleUnwindTcy: investment.investmentSaleUnwindTcy,
      investmentSaleUnwindLcy: investment.investmentSaleUnwindLcy,
      overallPnlLcy: round(overallPnlLcy, 2),
      equityPnlLcy: round(equityPnlLcy, 2),
      fxPnlLcy,
    };
  }

  if (
    considerationTypeHoldingClassType ===
      ConsiderationTypeHoldingClassTypes.NonLcyConsiderationNonLcyHoldingClass ||
    considerationTypeHoldingClassType ===
      ConsiderationTypeHoldingClassTypes.ThreeCYNonLcyConsiderationNonLcyHoldingClass
  ) {
    const totalConsiderationLcy = round(
      realisationTransactionRate
        ? totalConsiderationTcy / realisationTransactionRate
        : 0,
      2,
    );

    const overallPnlLcy = round(
      totalConsiderationLcy - investment.investmentSaleUnwindLcy,
      2,
    );

    const equityPnlLcy = overallPnlLcy - fxPnlLcy;

    return {
      totalConsiderationTcy,
      totalConsiderationLcy,
      investmentSaleUnwindTcy: investment.investmentSaleUnwindTcy,
      investmentSaleUnwindLcy: investment.investmentSaleUnwindLcy,
      overallPnlLcy: round(overallPnlLcy, 2),
      equityPnlLcy: round(equityPnlLcy, 2),
      fxPnlLcy: round(fxPnlLcy, 2),
    };
  }

  if (
    considerationTypeHoldingClassType ===
    ConsiderationTypeHoldingClassTypes.LcyConsiderationForNonLcyHoldingClass
  ) {
    const totalConsiderationLcy = totalConsiderationTcy;

    const overallPnlLcy =
      totalConsiderationLcy - investment.investmentSaleUnwindLcy;

    const equityPnlLcy = overallPnlLcy - fxPnlLcy;

    return {
      totalConsiderationTcy,
      totalConsiderationLcy,
      investmentSaleUnwindTcy: investment.investmentSaleUnwindTcy,
      investmentSaleUnwindLcy: investment.investmentSaleUnwindLcy,
      overallPnlLcy: round(overallPnlLcy, 2),
      equityPnlLcy: round(equityPnlLcy, 2),
      fxPnlLcy: round(fxPnlLcy, 2),
    };
  }

  return {
    totalConsiderationTcy,
    totalConsiderationLcy: 0,
    investmentSaleUnwindTcy: investment.investmentSaleUnwindTcy,
    investmentSaleUnwindLcy: investment.investmentSaleUnwindLcy,
    overallPnlLcy: 0,
    equityPnlLcy: 0,
    fxPnlLcy: 0,
  };
};

export type HoldingClassInvestmentConsiderationValues = {
  holdingClassId: string;
  investmentId: string;
  investmentDate: Date;
  investmentSaleUnwindTcy: number;
  investmentSaleUnwindLcy: number;
  receivedConsiderationTotal: number;
  escrowConsiderationTotal: number;
  earnoutConsiderationTotal: number;
  withholdingTaxPayable: number;
  currency: CurrencyCode;
};

export const calulateRealisedGainsAndFxPerInvestment = (
  holdingClassInvestment: Array<HoldingClassInvestmentConsiderationValues>,
  realisationCurrencyRate: CurrencyRateDetails | null,
  threeCyRealisationCurrencyRate: CurrencyRateDetails | null,
  realisationType: ConsiderationTypeHoldingClassTypes,
) => {
  const realisedGainsAndFxTransactions = holdingClassInvestment.map(
    ({
      holdingClassId,
      investmentId,
      receivedConsiderationTotal,
      escrowConsiderationTotal,
      earnoutConsiderationTotal,
      withholdingTaxPayable,
      investmentSaleUnwindTcy,
      investmentSaleUnwindLcy,
    }) =>
      calculateEquityAndFxGains(
        {
          holdingClassId,
          investmentId,
          receivedConsiderationTotal,
          escrowConsiderationTotal,
          earnoutConsiderationTotal,
          withholdingTaxPayable,
          investmentSaleUnwindTcy,
          investmentSaleUnwindLcy,
        },
        realisationCurrencyRate,
        threeCyRealisationCurrencyRate,
        realisationType,
      ),
  );

  return realisedGainsAndFxTransactions;
};
